import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import LangPicker from './LangPicker.jsx';
import Icon from './Icon.jsx';
import { muted, setMuted } from './alertPrefs.js';

/**
 * Panelin kabuğu: üstte çubuk, solda gezinme, sağ üstte kullanıcı.
 *
 * NEDEN BÖYLE:
 *  · Eskiden on bir bağlantı tek bir satırda akıyordu; telefonda üç satıra
 *    sarıp ekranın yarısını yiyordu. Dar ekranda gezinme ÇEKMECEYE girdi,
 *    geniş ekranda sabit kenar çubuğu oldu — ikisi de aynı liste.
 *  · Kullanıcı sağ üstte: hangi hesapla, hangi restoranda olduğunu görmek
 *    her ekranda lazım; çıkış ve dil de oraya toplandı.
 *  · Çekmece gezinince kendiliğinden kapanır (telefonda açık kalması
 *    "bağlantıya bastım, bir şey olmadı" hissi veriyordu).
 */

function initials(name, email) {
  const src = (name || email || '?').trim();
  const parts = src.split(/[\s@._-]+/).filter(Boolean);
  const two = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
  return (two || src[0] || '?').toUpperCase();
}

function UserMenu({ name, email, avatarUrl, subtitle, lang, onPickLang, onLogout, t }) {
  const [open, setOpen] = useState(false);
  // Uyarı sesi CİHAZA ait: kasa sesli, ofisteki tablet sessiz olabilsin.
  const [quiet, setQuiet] = useState(muted);
  const box = useRef(null);

  // Dışarı tıklayınca ve Esc ile kapanır: açık kalan menü mobilde içeriğin
  // üstünü kapatıyor.
  useEffect(() => {
    if (!open) return;
    const away = (e) => { if (!box.current?.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  return (
    <div className="usermenu" ref={box}>
      <button className="userbtn" onClick={() => setOpen(!open)} aria-expanded={open} aria-haspopup="menu">
        {avatarUrl
          ? <img className="avatar" src={avatarUrl} alt="" referrerPolicy="no-referrer" />
          : <span className="avatar fallback">{initials(name, email)}</span>}
        <span className="uname">{name || email}</span>
        <span className="caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="usercard" role="menu">
          <p className="uhead">{name || email}</p>
          <p className="muted">{email}</p>
          {subtitle && <p className="muted">{subtitle}</p>}
          <hr />
          <LangPicker lang={lang} onPick={onPickLang} style={{ width: '100%' }} />
          <button className="block" style={{ marginTop: 8 }}
            onClick={() => { setMuted(!quiet); setQuiet(!quiet); }}>
            <Icon n={quiet ? 'ban' : 'bell'} />{quiet ? t('alert.soundOff') : t('alert.soundOn')}
          </button>
          <button className="block" style={{ marginTop: 8 }} onClick={onLogout}>
            <Icon n="logout" />{t('nav.logout')}
          </button>
        </div>
      )}
    </div>
  );
}

export default function Shell({ nav, user, demo = false, lang, onPickLang, onLogout, t, children }) {
  const [drawer, setDrawer] = useState(false);
  const loc = useLocation();

  useEffect(() => { setDrawer(false); }, [loc.pathname]);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') setDrawer(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, []);

  return (
    <div className={drawer ? 'shell drawer-open' : 'shell'}>
      <header className="appbar">
        <button className="iconbtn burger" onClick={() => setDrawer(!drawer)}
          aria-label={t('nav.navigation')} aria-expanded={drawer}>
          <span /><span /><span />
        </button>
        <img className="applogo" src="/icon-32.png" alt="" width="26" height="26" />
        <span className="appname">OrderCEO</span>
        <span className="spacer" />
        {/* Yükseltme çağrısı ismin YANINDA: her ekranda görünür, gezinme
            listesini şişirmez. Dar ekranda yalnız simge kalır. */}
        {/* TEST ödeme kipi açıkken HER ekranda görünür: kasaya para girmediği
            unutulup akşam kapatılmayan bir kip, bir günlük ciro demek. */}
        {demo && (
          <Link className="testchip" to="/settings" title={t('set.demoTitle')}>
            <Icon n="playCircle" /><span>{t('nav.demoOn')}</span>
          </Link>
        )}
        {user.upgrade && (
          <Link className="upsell" to="/settings/billing" title={t('nav.upgrade')}>
            <Icon n="star" /><span>{t('nav.upgrade')}</span>
          </Link>
        )}
        <UserMenu {...user} lang={lang} onPickLang={onPickLang} onLogout={onLogout} t={t} />
      </header>

      <aside className="side" aria-label={t('nav.navigation')}>
        <nav>
          {nav.map(([to, label, end, icon]) => (
            <NavLink key={to} to={to} end={!!end} className={({ isActive }) => (isActive ? 'on' : '')}>
              <Icon n={icon} />{label}
            </NavLink>
          ))}
        </nav>
      </aside>

      {/* Çekmece açıkken arkaya tıklamak kapatır. */}
      <div className="backdrop" onClick={() => setDrawer(false)} aria-hidden="true" />

      <main className="content">{children}</main>
    </div>
  );
}
